import { Button, TextInput } from '@any-ui-react/core';
import { vestResolver } from '@hookform/resolvers/vest';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ZodError } from 'zod';

import { DatePicker } from '@/common/components/shared/date-picker/DatePicker';
import { ModalFooter } from '@/common/components/shared/modal-footer/ModalFooter';
import { toast } from '@/common/components/shared/toaster/toast';
import { EmployeeVisa, type EmployeeVisaType, VisaFormData, type VisaFormDataType } from '@/common/types';
import { DateUtils } from '@/common/utils';

import { visaFormValidationSuite } from './visa-form.validator';

export interface VisaFormProps {
  onSuccess: (data: EmployeeVisaType) => void;
  onCancel: () => void;
  visa?: EmployeeVisaType;
}

export const VisaForm = ({ visa, onSuccess, onCancel }: VisaFormProps) => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    setValue,
    clearErrors,
    control,
  } = useForm<VisaFormDataType>({
    defaultValues: VisaFormData.parse({ ...visa }),
    resolver: vestResolver(visaFormValidationSuite),
  });

  const visaIssuedDate = useWatch({ control, name: 'visaIssuedDate' });
  const visaExpiryDate = useWatch({ control, name: 'visaExpiryDate' });
  const workPermitIssuedDate = useWatch({ control, name: 'workPermitIssuedDate' });
  const workPermitExpiryDate = useWatch({ control, name: 'workPermitExpiryDate' });

  const { text: visaIssuedDateText, isFuture: visaIssuedDateIsFuture } = useMemo(
    () => DateUtils.getDifferenceString(visaIssuedDate),
    [visaIssuedDate],
  );
  const { text: visaExpiryDateText, isFuture: visaExpiryDateIsFuture } = useMemo(
    () => DateUtils.getDifferenceString(visaExpiryDate),
    [visaExpiryDate],
  );
  const { text: workPermitIssuedDateText, isFuture: workPermitIssuedDateIsFuture } = useMemo(
    () => DateUtils.getDifferenceString(workPermitIssuedDate),
    [workPermitIssuedDate],
  );
  const { text: workPermitExpiryDateText, isFuture: workPermitExpiryDateIsFuture } = useMemo(
    () => DateUtils.getDifferenceString(workPermitExpiryDate),
    [workPermitExpiryDate],
  );

  const onSubmit = useMemo(
    () =>
      handleSubmit(async formValues => {
        try {
          onSuccess(EmployeeVisa.parse({ ...formValues, id: visa?.id }));
        } catch (e) {
          if (e instanceof ZodError) {
            const message = e.errors.map(it => `${it.path.join('.')}: ${it.message}`).join('\n');
            toast.error(message);
          }
        }
      }),
    [handleSubmit, onSuccess, visa?.id],
  );

  return (
    <form noValidate={true}>
      <fieldset className="form-fieldset w-full">
        <div className="col-span-2 font-semibold text-lg">Visa</div>

        <TextInput
          {...register('visaType')}
          className="col-span-2 sm:col-span-1"
          error={errors.visaType?.message}
          label="Visa Type"
          placeholder="Visa Type"
        />

        <TextInput
          {...register('visaNumber')}
          className="col-span-2 sm:col-span-1"
          error={errors.visaNumber?.message}
          label="Visa Number"
          placeholder="Visa Number"
        />

        <div className="col-span-2 sm:col-span-1">
          <DatePicker
            clearable={true}
            error={errors.visaIssuedDate?.message}
            label="Visa issued date"
            placeholder="yyyy-MM-dd"
            value={visaIssuedDate || undefined}
            onChange={value => {
              setValue('visaIssuedDate', DateUtils.formatDate(value), {
                shouldDirty: true,
              });
              clearErrors('visaIssuedDate');
            }}
          />
          <div className="text-gray-7 text-xs ml-3">
            {visaIssuedDateText}
            {!visaIssuedDateIsFuture ? ' ago' : ''}
          </div>
        </div>

        <div className="col-span-2 sm:col-span-1">
          <DatePicker
            clearable={true}
            error={errors.visaExpiryDate?.message}
            label="Visa expiry date"
            placeholder="yyyy-MM-dd"
            value={visaExpiryDate || undefined}
            onChange={value => {
              setValue('visaExpiryDate', DateUtils.formatDate(value), {
                shouldDirty: true,
              });
              clearErrors('visaExpiryDate');
            }}
          />
          <div className="text-gray-7 text-xs ml-3">
            {visaExpiryDateText}
            {!visaExpiryDateIsFuture ? ' ago' : ''}
          </div>
        </div>

        <div className="col-span-2 font-semibold text-lg mt-2">Work permit</div>

        <TextInput
          {...register('workPermitNumber')}
          className="col-span-2"
          error={errors.workPermitNumber?.message}
          label="Work permit number"
          placeholder="Work permit number"
        />

        <div className="col-span-2 sm:col-span-1">
          <DatePicker
            clearable={true}
            error={errors.workPermitIssuedDate?.message}
            label="Work permit issued date"
            placeholder="yyyy-MM-dd"
            value={workPermitIssuedDate || undefined}
            onChange={value => {
              setValue('workPermitIssuedDate', DateUtils.formatDate(value), {
                shouldDirty: true,
              });
              clearErrors('workPermitIssuedDate');
            }}
          />
          <div className="text-gray-7 text-xs ml-3">
            {workPermitIssuedDateText}
            {!workPermitIssuedDateIsFuture ? ' ago' : ''}
          </div>
        </div>

        <div className="col-span-2 sm:col-span-1">
          <DatePicker
            clearable={true}
            error={errors.workPermitExpiryDate?.message}
            label="Work permit expiry date"
            placeholder="yyyy-MM-dd"
            value={workPermitExpiryDate || undefined}
            onChange={value => {
              setValue('workPermitExpiryDate', DateUtils.formatDate(value), {
                shouldDirty: true,
              });
              clearErrors('workPermitExpiryDate');
            }}
          />
          <div className="text-gray-7 text-xs ml-3">
            {workPermitExpiryDateText}
            {!workPermitExpiryDateIsFuture ? ' ago' : ''}
          </div>
        </div>

        {errors.allFields && <span className="text-sm text-red-6">{errors.allFields.message}</span>}
      </fieldset>

      <ModalFooter spacerSize={-1}>
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="bg-blue-6" loading={isSubmitting} type="submit" onClick={onSubmit}>
          Save
        </Button>
      </ModalFooter>
    </form>
  );
};
